import { convertToRaw } from "draft-js";
import { List } from "immutable";
import { contentStateFromString } from "@utils/richText/converters";
import { mediaFromDto } from "@domain/media/dtos";
import { PersonDto, personFromDto, ProfileDto } from "@domain/user/dtos";
import { Author } from "@domain/lms/content";
import { ExerciseDto, exerciseFromDto, exerciseToDto } from "@domain/lms/course/exercise/dtos";
import { Quiz, QuizParticipant, QuizParticipantAnswer } from "./quiz";
import { QuizDraft } from "./quizDraft";

export interface QuizDraftDto extends Omit<QuizDraft, "description" | "exercises" | "workspaceIds"> {
	description: string;
	exercises: ExerciseDto[];
	workspaceIds?: string[];
}
export interface QuizDto extends Omit<Quiz, "author" | "publisher" | "description" | "exercises" | "lastUpdateDate"> {
	author?: PersonDto;
	publisher?: PersonDto;
	description: string;
	exercises: ExerciseDto[];
	lastUpdateDate: string;
}

export interface QuizParticipantAnswerDto extends Omit<QuizParticipantAnswer, "quiz"> {
	quiz: QuizDto;
}
export function quizDraftToDto(draft: QuizDraft): QuizDraftDto {
	return {
		...draft,
		description: JSON.stringify(convertToRaw(draft.description)),
		exercises: draft.exercises.map(exerciseToDto).toArray(),
		workspaceIds: draft.workspaceIds?.toArray(),
	};
}

export function quizFromDto(dto: QuizDto): Quiz {
	return {
		...dto,
		author: dto.author ? (personFromDto(dto.author) as Author) : undefined,
		publisher: dto.publisher ? personFromDto(dto.publisher) : undefined,
		exercises: List(dto.exercises.map(exerciseFromDto)),
		description: contentStateFromString(dto.description),
		lastUpdateDate: new Date(dto.lastUpdateDate),
		level: dto.level || undefined,
	};
}

export function QuizParticipantAnswerFromDto(dto: QuizParticipantAnswerDto): Quiz {
	return {
		...quizFromDto(dto.quiz),
		participantDetails: dto.participantDetails,
	};
}

export function QuizParticipantFromDto(dto: QuizParticipantDto): QuizParticipant {
	const {
		avatar,
		avatarOriginal,
		banner,
		description,
		followingMeSince,
		followedByMeSince,
		memberSince,
		type,
		presentation,
		insightCount,
		carouselCount,
		quizCount,
		courseCount,
		avatarCrop,
	} = dto;
	return {
		...dto,
		avatar: avatar ? mediaFromDto(avatar) : undefined,
		avatarOriginal: avatarOriginal ? mediaFromDto(avatarOriginal) : undefined,
		avatarCrop: avatarCrop ? mediaFromDto(avatarCrop) : undefined,
		banner: banner ? mediaFromDto(banner) : undefined,
		followingMeSince: followingMeSince && followingMeSince != null ? new Date(followingMeSince) : undefined,
		followedByMeSince: followedByMeSince && followedByMeSince != null ? new Date(followedByMeSince) : undefined,
		memberSince: memberSince ? new Date(memberSince) : undefined,
		description: description ?? "",
		type: type ?? "USER",
		presentation: presentation ? contentStateFromString(presentation) : undefined,
		contentCount: (insightCount ?? 0) + (courseCount ?? 0) + (carouselCount ?? 0) + (quizCount ?? 0),
	};
}

export interface QuizResultDto {
	score: number;
	exercises: ExerciseDto[];
}

export interface QuizParticipantDto extends ProfileDto {
	score: number;
}
